/**
 * The player pages' own settings (docs/analytics-api-contract.md, Phase 3b): the values each route
 * accepts, kept light so `routes.ts` can validate its search params without loading the pages.
 */
import { MAX_DAYS_BY_GRAIN } from './filters';

/** When the warehouse starts: first plays before it are unknowable (the contract's `meta.firstSeenSince`). */
export const FIRST_SEEN_SINCE = '2026-08-23';

/** The widest window any player route takes (the month cap); each grain narrows it further. */
export const PLAYER_MAX_DAYS = MAX_DAYS_BY_GRAIN.month;

export const PLAYER_GRAINS = ['day', 'week', 'month'] as const;
export type PlayerGrain = (typeof PLAYER_GRAINS)[number];

export const GRAIN_LABEL: Record<PlayerGrain, string> = { day: 'Day', week: 'Week', month: 'Month' };

/** The grains the server accepts for a window of `days` (spec §7.4.3 caps). */
export const grainsFor = (days: number): PlayerGrain[] => PLAYER_GRAINS.filter((g) => days <= MAX_DAYS_BY_GRAIN[g]);

export const HOUR_METRICS = ['activePlayers', 'rounds', 'turnover'] as const;
export type HourMetric = (typeof HOUR_METRICS)[number];

export const RETENTION_PERIODS = [4, 8, 12, 26] as const;
export const CHURN_GRAINS = ['week', 'month'] as const;
export type ChurnGrain = (typeof CHURN_GRAINS)[number];
export const INACTIVE_DAYS = [7, 14, 30, 60] as const;

export const FUNNEL_BY = ['none', 'operatorId', 'firstGameId'] as const;
export type FunnelBy = (typeof FUNNEL_BY)[number];

export const LTV_COHORTS = ['week', 'month'] as const;
export type LtvCohortGrain = (typeof LTV_COHORTS)[number];
export const LTV_DAYS = [7, 14, 30, 60, 90] as const;
export const GAP_MINUTES = [5, 10, 15, 30, 60, 120] as const;

/** A number from the URL if it's one of the offered values, else the default. */
export const oneOfNumbers = <T extends number>(value: number | undefined, allowed: readonly T[], fallback: T): T =>
  allowed.find((a) => a === value) ?? fallback;

/** The player rankings (spec 2026-09-27): one tab each, ranked on the server. */
export const RANKING_TABS = ['topRollers', 'highRollers', 'loyal', 'broadest', 'winners', 'losers', 'flagged'] as const;
export type RankingTab = (typeof RANKING_TABS)[number];

export const RANKING_TAB_LABEL: Record<RankingTab, string> = {
  topRollers: 'Top-rollers',
  highRollers: 'High-rollers',
  loyal: 'Loyal',
  broadest: 'Broadest',
  winners: 'Top winners',
  losers: 'Top losers',
  flagged: 'Flagged',
};

/** Rankings read every round of the period per wallet: the daily-grain cap. */
export const RISK_MAX_DAYS = MAX_DAYS_BY_GRAIN.day;
export const RANKING_PAGE_SIZE = 25;
/** Each tab ranks at most this many wallets. */
export const RANKING_LIMIT = 100;
